import React, {createRef} from 'react';
import ChatEventCodes from "../utils/ChatEventCodes";
import InteractiveMessage from "./Components/InteractiveMessage";

/**
 * Class to display the list view template. This can be either interactable elements
 * or not. It has been stated that they won't be mixed but if for some reason they are,
 * this will need modificaiton.
 */
export default class ListViewMessage extends InteractiveMessage {
    constructor(props) {
        super(props);

        this.sendInput = this.sendInput.bind(this);
        this.showMoreRef = createRef();
        this.showMore = this.showMore.bind(this);
        this.ariaLiveRef = createRef();
        this.maxNewElements = 10;
        this.nextFocusedElementRef = undefined;

        this.state = {
            displayedElements: [],
            startingIndex: 0
        }
    }


    componentDidMount() {
        super.componentDidMount();
        //set state displayed custom times to the max of 10
        this.setState({displayedElements: this.getElements()});
    }

    /**
     * Loops through the elements provided in the props. If there are no elements, or we have reached
     * the end, return an empty array. If not, then add the max new elements against the current lookup
     * index. If that value is greater than the length of the full collection, then return what remains.
     * Else return from the current lookup index to the max number.
     * @returns The collection of new elements to be shown.
     */
    getElements() {
        const elementsLength = this.props.payload.elements.length;
        if (elementsLength === 0 || this.state.startingIndex >= elementsLength) {
            return [];
        }

        let newElements = [];

        if (this.state.startingIndex + this.maxNewElements >= elementsLength) {
            newElements = this.props.payload.elements.slice(this.state.startingIndex);
        }
        else {
            newElements = this.props.payload.elements.slice(this.state.startingIndex, this.maxNewElements + this.state.startingIndex);
        }

        return newElements;
    }

    /**
     * Returns the matching masked user response when this was a previous message in
     * the chat history. If no match, then return the value from history.
     */
    static getMaskedHistory(payload, currentValue) {
        for (const element of payload.elements) {
            if (element.default_action?.title && (element.default_action.payload === currentValue || element.default_action.payload === currentValue.toString())) {
                return element.default_action.title;
            }
        }
        return currentValue;
    }

    /**
     * Emits the event to send the input with the payload of the button.
     */
    sendInput(element) {
        const data = {
            botPayload: element.default_action.payload,
            displayMessage: element.default_action.title
        };
        document.dispatchEvent(new MessageEvent(ChatEventCodes.UserMaskedInput, {data: data}));
    }

    /**
     * Returns the full list block of the passed through listView elements.
     * If show more has been used, then apply a focus ref on the first element
     * of the new N loaded in to maintain screen reader context.
     */
    renderList() {
        if (!this.state.displayedElements || this.state.displayedElements.length === 0) {
            return '';
        }
        return <ul className='list-view__items'>
                        {this.state.displayedElements.map((element, index) => {
                            if (index === this.state.startingIndex) {
                                return <li className='list-item' key={element} ref={this.nextFocusedElementRef} tabindex='-1'>{this.renderListItem(element)}</li>
                            }
                            return <li className='list-item' key={element}>{this.renderListItem(element)}</li>
                        })}
                </ul>
    }

    /**
     * Returns the list view element block. If the element has a default action,
     * then it is an interactab;e button. If not, then it is rendered statically.
     * @param {Object} element 
     * @returns 
     */
    renderListItem(element) {
        if (element.default_action && Object.keys(element.default_action).length > 0) {
            return this.renderListItemButton(element);
        }
        return this.renderListItemDiv(element);
    }

    /**
     * Renders an interactable button element for the list view.
     */
    renderListItemButton(element) {
        if (this.props.message.isChatHistory || this.state.userInteracted) {
            return '';
        }
        return <button key={element}
                    className='list-view__element interaction-button'
                    title={element.default_action.title}
                    onClick={() => this.sendInput(element)}>
                    <div className="list-view-item__title">{element.title}</div>
                    <div className='list-view-item__subtitle'>{element.subtitle}</div>
                    <div className='list-view-item__value'>{element.value}{this.renderPendingText(element)}</div>
                </button>;
    }

    /**
     * Renders a static element for the list view.
     */
    renderListItemDiv(element) {
        return <div key={element}
                    className='list-view__element no-action'>
                    <div className="list-view-item__title">{element.title}</div>
                    <div className='list-view-item__subtitle'>{element.subtitle}</div>
                    <div className='list-view-item__value'>{element.value}{this.renderPendingText(element)}</div>
                </div>
    }

    /**
     * Renders out text regarding if a transaction is pending or not. There are other options for
     * element.meta.status around posted or declined but the only current ask is to display pending.
     * Also note this is not currently working for credit card/line transactions.
     */
    renderPendingText(element) {
        return (element.meta?.status && element.meta.status.toLowerCase() === 'pending') ? <span className='pending-value'>pending</span> : '';
    }

    /**
     * Renders the show more link/button if there are additional elements that
     * have been returned from the service.
     */
    renderShowMoreButton() {
        if (this.state.startingIndex === this.props.payload.elements.length || this.state.startingIndex + this.maxNewElements >= this.props.payload.elements.length) {
            return '';
        }

        return <a href='#' role='button' className='show-more-button' onClick={(e) => this.showMore(e)} ref={this.showMoreRef}>Show more</a>
    }

    /**
     * Handle the click on the show more link to display more available elements.
     * This also updates a hidden aria-live live region in the template that informs the
     * user how many more timesplots have been shown.
     * @param {Event} e
     */
    showMore(e) {
        e.preventDefault();
        const nextIndex = this.state.startingIndex + this.maxNewElements;
        this.setState({startingIndex: nextIndex, focusedElement: undefined}, () => {
            const newElements = this.getElements();
            const updatedElements = this.state.displayedElements.concat(newElements);
            if (this.ariaLiveRef.current) {
                this.ariaLiveRef.current.innerText = '';
                this.ariaLiveRef.current.innerText = newElements.length === 1 ? `${newElements.length} more item displayed.` : `${newElements.length} more items displayed.`;
            }
            this.nextFocusedElementRef = createRef();
            this.setState({displayedElements: updatedElements}, () => {
                /**
                 * Need to focus on the next list item in the N new that are renderd out so the user can continue
                 * to read through the list from where they left off. Either the focus would follow to new show more button
                 * OR go to the <body> tag which isn't behavior that would be expected.
                 */
                if (this.nextFocusedElementRef.current) {
                    this.nextFocusedElementRef.current.focus();
                }
            });
        });
    }
    
    render() {
        return(
            <div className={this.generateSenderClassName('list-view')}>
                {this.generateSenderMessage()}
                <div className='list-view__text message-text'>
                    <div className='list-view__heading' dangerouslySetInnerHTML={this.evaluateMarkdown(this.props.payload.text)}>
                    </div>
                    <div class='list-view__list-wrapper' ref={this.interactionRef}>
                        {this.renderList()}
                    </div>
                    {this.renderShowMoreButton()}
                </div>
                <div className='vh' aria-live='polite' ref={this.ariaLiveRef}></div>
            </div>
        )
    }
}